import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ApWordmark from './ApWordmark';
import { usePublicSession } from '../../contexts/PublicSessionContext';
import { supabase } from '../../lib/supabase';
import { prepareOAuthRedirect, clearAuthInProgressIfOwned, clearAuthRedirectFlags } from '../../utils/authFlow';
import { needsOnboarding } from '../../utils/onboardingRules';
import folioCompass from '../../assets/constellation-folio/folio-compass.png';
import folioConstellation from '../../assets/constellation-folio/folio-constellation.png';
import './platform.css';
import './ink-wash-product.css';
import './auth.css';

function safeRedirectPath(value, fallback) {
  const candidate = String(value || '').trim();
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return fallback;
  if (['/signin', '/signup'].includes(candidate.split('?')[0])) return fallback;
  return candidate;
}

export default function ApAuthScreen({ mode }) {
  const isSignUp = mode === 'sign-up';
  const { signInWithGoogle, user, profile, loading } = usePublicSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [error, setError] = useState(
    searchParams.get('auth_error') === 'stale'
      ? 'Your sign-in link expired. Start again and we will reconnect you safely.'
      : '',
  );
  const redirectingRef = useRef(false);
  const redirectPath = isSignUp
    ? safeRedirectPath(searchParams.get('redirect'), '/onboarding')
    : '/home';
  const signedInRedirectPath = isSignUp && !needsOnboarding(profile) ? '/home' : redirectPath;
  const redirectTargetPathname = signedInRedirectPath.split(/[?#]/)[0];

  useEffect(() => {
    if (
      loading ||
      !user ||
      redirectingRef.current ||
      location.pathname === redirectTargetPathname
    ) {
      return;
    }

    redirectingRef.current = true;
    navigate(signedInRedirectPath, { replace: true });
  }, [loading, location.pathname, navigate, redirectTargetPathname, signedInRedirectPath, user]);

  useEffect(() => () => clearAuthInProgressIfOwned(), []);

  async function handleGoogle() {
    if (user) {
      if (!redirectingRef.current && location.pathname !== redirectTargetPathname) {
        redirectingRef.current = true;
        navigate(signedInRedirectPath, { replace: true });
      }
      return;
    }
    const authPrep = await prepareOAuthRedirect({
      intendedRoute: redirectPath,
      checkSession: async () => Boolean((await supabase.auth.getSession()).data?.session),
    });
    if (!authPrep.ok) { setError('Authentication is already in progress in another tab.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await signInWithGoogle({ forceAccountSelection: true });
    } catch (cause) {
      setError(cause?.message || 'Google sign-in could not start.');
      setSubmitting(false);
      clearAuthRedirectFlags();
    }
  }

  return (
    <main className="ap-auth-page">
      <header className="ap-auth-header">
        <ApWordmark />
        <Link to="/"><ArrowLeft aria-hidden="true" /> Back to home</Link>
      </header>

      <div className="ap-auth-ornaments" aria-hidden="true">
        <img className="ap-auth-etching is-constellation" src={folioConstellation} alt="" />
        <img className="ap-auth-etching is-compass" src={folioCompass} alt="" />
      </div>

      <div className="ap-auth-grid">
        <section className="ap-auth-copy">
          <p className="ap-eyebrow">{isSignUp ? 'Your AP study space' : 'Student account'}</p>
          <h1>{isSignUp ? 'Start with the courses you actually take.' : 'Welcome back to your study desk.'}</h1>
          <p>{isSignUp ? 'Create your account, then choose your AP subjects and build a study path around your goals.' : 'Sign in to return to your saved subjects, Tutor conversations, flashcards, and course progress.'}</p>

          {isSignUp ? (
            <ol className="ap-auth-route" aria-label="Account setup steps">
              <li className="is-active"><span>1</span><strong>Create account</strong></li>
              <li><span>2</span><strong>Choose subjects</strong></li>
              <li><span>3</span><strong>Build your plan</strong></li>
            </ol>
          ) : (
            <ul className="ap-auth-return-list">
              <li><Check /> Your AP courses stay organised</li>
              <li><Check /> Your Tutor history is ready</li>
              <li><Check /> Your next study step is waiting</li>
            </ul>
          )}
        </section>

        <section className="ap-auth-card" aria-labelledby="ap-auth-card-title">
          <span className="ap-auth-card-star" aria-hidden="true"><Sparkles /></span>
          <p className="ap-eyebrow">{isSignUp ? 'Get started' : 'Secure sign in'}</p>
          <h2 id="ap-auth-card-title">{isSignUp ? 'Create your account' : 'Sign in to APDoneRight'}</h2>
          <div className="ap-auth-card-rule" aria-hidden="true"><span>✦</span></div>
          <p>Continue securely with Google.</p>
          {error ? <div className="ap-auth-error" role="alert">{error}</div> : null}
          <button className="ap-google-button" type="button" disabled={submitting} onClick={handleGoogle}>
            <span className="ap-google-mark" aria-hidden="true"><FcGoogle /></span>
            <span>{submitting ? 'Connecting…' : 'Continue with Google'}</span>
            <ArrowRight />
          </button>
          <div className="ap-auth-trust"><ShieldCheck aria-hidden="true" /><span>Secure Google sign-in</span><span aria-hidden="true">·</span><span>Privacy protected</span></div>
          <p className="ap-auth-switch">
            {isSignUp ? 'Already have an account?' : 'New to APDoneRight?'}{' '}
            <Link to={isSignUp ? '/signin' : '/signup?redirect=%2Fonboarding'}>{isSignUp ? 'Sign in' : 'Get started'}</Link>
          </p>
        </section>
      </div>

      <footer className="ap-auth-footer">
        <span>By continuing, you agree to our terms.</span>
        <nav aria-label="Legal"><Link to="/terms-of-service">Terms of Use</Link><Link to="/privacy-policy">Privacy Policy</Link></nav>
      </footer>
    </main>
  );
}
